import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export interface AdminOrganizationProgress {
  organizationId: number
  organizationName: string
  organizationType: string
  subdomain: string | null
  totalLearners: number
  notStartedCount: number
  inProgressCount: number
  completedCount: number
  averageProgressRate: number
  completionRate: number
  totalClasses: number
  activeClasses: number
}

export interface AdminLearnerProgress {
  userId: number
  fullName: string
  email: string
  organizationName: string
  organizationId: number
  departmentName: string | null
  position: string | null
  className: string
  classId: number
  classProductId: number | null
  progressPercent: number
  lessonQuizAccuracy: number | null
  checkupQuizAccuracy: number | null
  preAiqCompleted: boolean
  postAiqCompleted: boolean
  aiqGrowthRate: number | null
  learningStatus: string
}

export interface AdminLearnerProgressList {
  learners: AdminLearnerProgress[]
  totalCount: number
}

export interface LearnerProgressDetailModalData {
  userId: number
  fullName: string
  className: string
  progressPercent: number
  learningStatus: string
  summary: {
    totalLessons: number
    completedLessons: number
    lessonQuizAccuracy: number | null
    checkupQuizAccuracy: number | null
    lastAccessedAt: string | null
  }
  courses: CourseProgressDetail[]
}

export interface CourseProgressDetail {
  contentId: number
  title: string
  status: string
  progressPercent: number
  modules: ModuleProgressDetail[]
}

export interface ModuleProgressDetail {
  contentId: number
  title: string
  status: string
  progressPercent: number
  completedChildren: number
  totalChildren: number
  lessons: LessonProgressDetail[]
}

export interface LessonProgressDetail {
  contentId: number
  title: string
  status: string
  components: ComponentProgressDetail[]
}

export interface ComponentProgressDetail {
  type: string
  location: string
  status: string
  completionCount: number
  quizScore: number | null
  quizPassed: boolean | null
  videoWatchSeconds: number | null
}

// ============================================================================
// API
// ============================================================================

export const learningReportApi = {
  getOrganizationProgress: () =>
    apiClient.get<ApiResponse<AdminOrganizationProgress[]>>('/admin/reports/progress/by-organization'),

  getLearnerProgress: (params?: {
    organizationId?: number
    classId?: number
    status?: string
    search?: string
  }) =>
    apiClient.get<ApiResponse<AdminLearnerProgressList>>('/admin/reports/progress/learners', { params }),

  getLearnerProgressDetail: (userId: number, classProductId: number) =>
    apiClient.get<ApiResponse<LearnerProgressDetailModalData>>(
      `/admin/reports/progress/learners/${userId}/detail`,
      { params: { classProductId } }
    ),
}
